import { useState, useRef, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import { Plus, Search, BarChart3 } from 'lucide-react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useResourcesForCanvas, useResourceActions } from '../../stores';
import type { ResourceDefinition } from '../../types';
import { ResourceModal } from '../../resources/components/ResourceModal';
import { ResourceCatalogMenu } from '../../resources/components/ResourceCatalogMenu';
import { ResourceCanvasOverviewPanel } from './ResourceCanvasOverviewPanel';
import { formatFieldValue } from '../../lib/formatFieldValue';
import { createResourceFromTemplate } from '../../resources/resourceCatalog';

export const ResourceSidebarPanel = memo(function ResourceSidebarPanel() {
  const { t } = useTranslation();
  const { uiStore, boardId } = useCanvasStores();
  const resources = useResourcesForCanvas(boardId);
  const activeResourceId = useSelector(uiStore, s => (s.context as any).resourceDetailResourceId);
  const resourceActions = useResourceActions();
  const [searchText, setSearchText] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [catalogMenuOpen, setCatalogMenuOpen] = useState(false);
  const plusButtonRef = useRef<HTMLButtonElement>(null);

  const filteredResources = useMemo(() => {
    if (!searchText) return resources;
    const lower = searchText.toLowerCase();
    return resources.filter(r => {
      if (r.name.toLowerCase().includes(lower)) return true;
      if (r.category.toLowerCase().includes(lower)) return true;
      if (r.fields.some(f => f.key.toLowerCase().includes(lower) || String(f.value).toLowerCase().includes(lower))) return true;
      return false;
    });
  }, [resources, searchText]);

  // Group by scope
  const grouped = useMemo(() => {
    const global: ResourceDefinition[] = [];
    const board: ResourceDefinition[] = [];
    const canvas: ResourceDefinition[] = [];
    for (const r of filteredResources) {
      if (r.scope.type === 'global') global.push(r);
      else if (r.scope.type === 'board') board.push(r);
      else canvas.push(r);
    }
    return { global, board, canvas };
  }, [filteredResources]);

  const handleOpenEdit = useCallback((resourceId: string) => {
    uiStore.send({ type: 'resourceDetail.open', resourceId } as any);
  }, [uiStore]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, resourceId: string) => {
    e.dataTransfer.setData('application/x-canvas-resource-badge', JSON.stringify({ resourceId }));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const defaultScope: ResourceDefinition['scope'] = { type: 'board', boardId };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Search + Add */}
      <div style={{ padding: '6px 8px', display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={10} style={{ position: 'absolute', left: 6, top: 6, color: '#94a3b8' }} />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('resource.sidebar.search')}
            style={{
              width: '100%', fontSize: 10, padding: '3px 6px 3px 20px',
              border: '1px solid #e2e8f0', borderRadius: 4, outline: 'none',
              backgroundColor: '#fff', color: '#1f2937',
            }}
          />
        </div>
        <button
          onClick={() => setOverviewOpen(true)}
          title={t('resource.canvasOverview.title')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, border: '1px solid #e2e8f0', borderRadius: 4,
            background: '#fff', cursor: 'pointer', color: '#6366f1',
          }}
        >
          <BarChart3 size={12} />
        </button>
        <button
          ref={plusButtonRef}
          onClick={() => setCatalogMenuOpen(v => !v)}
          title={t('resource.sidebar.newResource')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, border: '1px solid #e2e8f0', borderRadius: 4,
            background: '#fff', cursor: 'pointer', color: '#3b82f6',
          }}
        >
          <Plus size={12} />
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
        {filteredResources.length === 0 ? (
          <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', padding: 16 }}>
            {t('resource.sidebar.noResults')}
          </div>
        ) : (
          <>
            {grouped.global.length > 0 && (
              <ResourceScopeGroup label={t('resource.sidebar.global')} resources={grouped.global} activeResourceId={activeResourceId} onOpen={handleOpenEdit} onDragStart={handleDragStart} />
            )}
            {grouped.board.length > 0 && (
              <ResourceScopeGroup label={t('resource.sidebar.board')} resources={grouped.board} activeResourceId={activeResourceId} onOpen={handleOpenEdit} onDragStart={handleDragStart} />
            )}
            {grouped.canvas.length > 0 && (
              <ResourceScopeGroup label={t('resource.sidebar.canvas')} resources={grouped.canvas} activeResourceId={activeResourceId} onOpen={handleOpenEdit} onDragStart={handleDragStart} />
            )}
          </>
        )}
      </div>

      {/* Modal -- only for creating new Resources */}
      <ResourceModal
        isOpen={createModalOpen}
        onClose={handleCloseCreateModal}
        defaultScope={defaultScope}
      />

      {/* Canvas Resource Overview Panel */}
      <ResourceCanvasOverviewPanel
        isOpen={overviewOpen}
        onClose={() => setOverviewOpen(false)}
      />

      {/* Catalog Quick-Add Menu */}
      <ResourceCatalogMenu
        isOpen={catalogMenuOpen}
        onClose={() => setCatalogMenuOpen(false)}
        anchorRef={plusButtonRef}
        onSelectTemplate={(template) => {
          const resource = createResourceFromTemplate(template, defaultScope);
          resourceActions.addResource(resource);
          setCatalogMenuOpen(false);
        }}
        onNewBlank={() => {
          setCatalogMenuOpen(false);
          setCreateModalOpen(true);
        }}
      />
    </div>
  );
});

// ResourceScopeGroup
function ResourceScopeGroup({
  label,
  resources,
  activeResourceId,
  onOpen,
  onDragStart,
}: {
  label: string;
  resources: ResourceDefinition[];
  activeResourceId: string | null;
  onOpen: (resourceId: string) => void;
  onDragStart: (e: React.DragEvent, resourceId: string) => void;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {label}
      </div>
      {resources.map(resource => {
        const isActive = resource.id === activeResourceId;
        const primaryField = resource.fields.find(f => f.showOnBadge);
        return (
          <div
            key={resource.id}
            draggable
            onDragStart={(e) => onDragStart(e, resource.id)}
            onClick={() => onOpen(resource.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
              borderRadius: 4, cursor: 'pointer', marginBottom: 1,
              backgroundColor: isActive ? '#eff6ff' : 'transparent',
            }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {/* Color dot */}
            <span
              style={{
                width: 6, height: 6, borderRadius: '50%',
                backgroundColor: resource.color || '#6366f1',
                flexShrink: 0,
              }}
            />
            {/* Name */}
            <span style={{ fontSize: 10, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {resource.name}
            </span>
            {/* Primary value badge */}
            {primaryField && (
              <span
                style={{
                  fontSize: 8, padding: '1px 4px', borderRadius: 3,
                  backgroundColor: (resource.color || '#6366f1') + '20',
                  color: resource.color || '#6366f1',
                  fontWeight: 500, flexShrink: 0,
                }}
              >
                {formatFieldValue(primaryField)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
